import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/shared/lib/api'

// ── Profile ─────────────────────────────────────────────
export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { full_name: string }) => {
      const res = await api.patch('/settings/profile', data)
      return res.data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth'] }),
  })
}

// ── Workspace ───────────────────────────────────────────
export function useUpdateWorkspace() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const res = await api.patch('/settings/workspace', data)
      return res.data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  })
}

// ── Billing ─────────────────────────────────────────────
export function useBillingInfo() {
  return useQuery({
    queryKey: ['settings', 'billing'],
    queryFn: async () => {
      const { data } = await api.get('/settings/billing')
      return data.data
    },
  })
}

export function useCreateCheckout() {
  return useMutation({
    mutationFn: async (plan: string) => {
      const { data } = await api.post(`/billing/checkout?plan=${plan}`)
      return data
    },
  })
}

export function useCreatePortal() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/billing/portal')
      return data
    },
  })
}

// ── Google Accounts ─────────────────────────────────────
export function useGoogleAccounts() {
  return useQuery({
    queryKey: ['settings', 'google-accounts'],
    queryFn: async () => {
      const { data } = await api.get('/gfo/accounts')
      return data.data
    },
  })
}

export function useConnectGoogle() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.get('/settings/google/connect')
      return data.data
    },
  })
}

export function useDisconnectGoogle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (accountId: string) => {
      await api.delete(`/settings/google/${accountId}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'google-accounts'] }),
  })
}

// ── Notifications ───────────────────────────────────────
export function useNotificationPrefs() {
  return useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: async () => {
      const { data } = await api.get('/settings/notifications')
      return data.data
    },
  })
}

export function useUpdateNotificationPrefs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (prefs: {
      email_alerts: boolean
      email_weekly_report: boolean
      inapp_alerts: boolean
    }) => {
      const { data } = await api.patch('/settings/notifications', prefs)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'notifications'] }),
  })
}

// ── Tenant Info ─────────────────────────────────────────
export function useTenantInfo() {
  return useQuery({
    queryKey: ['settings', 'tenant'],
    queryFn: async () => {
      const { data } = await api.get('/tenants/me')
      return data
    },
  })
}

